import * as THREE from "three";
import { gsap } from "gsap";
import threeSketch from "../../../libs/three-sketch";
import EmitEvent, {
  HT_RESIZE,
  PAGE_ENTER,
  PAGE_LOADED,
  SMOOTH_SCROLLING,
} from "../../../libs/emit-event";
import { MathLerp, MathMap } from "../../../libs/math-utils";
import services from "../../../libs/services";
import { ProjectItem } from "./project-item";
import { Drag } from "../../system/drag/drag";

export class ShortcodeProjectsPage {
  constructor() {
    this.DOM = { main: document.querySelector(".js-projectsPage") };
    if (!this.DOM.main) return;

    this.DOM.container = this.DOM.main.querySelector(
      ".js-projectsPage_container"
    );
    this.DOM.dragger = this.DOM.main.querySelector(".js-projectsPage_wrapper");
    this.DOM.borderActive = this.DOM.main.querySelector(
      ".js-projectsPage_thumbsActive"
    );
    this.DOM.thumbnailItems = this.DOM.main.querySelectorAll(
      ".js-projectsPage_thumbs_item"
    );
    this.DOM.controlLeft = this.DOM.main.querySelector(
      ".js-projectsPage_control__left"
    );
    this.DOM.controlRight = this.DOM.main.querySelector(
      ".js-projectsPage_control__right"
    );

    console.log(this.DOM.controlLeft);

    this.uniforms = {
      u_strength: { value: 22.5 },
    };

    this.projects = [];
    this.strength = {
      current: 0,
      dragging: 0,
      draggingVal: 0,
      ease: 0.1,
      last: 0,
    };

    this.styles = {};
    this.itemActive = 1;

    this.handleScrolling = this.handleScrolling.bind(this);
    this.loop = this.loop.bind(this);
    this.onDragging = this.onDragging.bind(this);
    this.getProp = this.getProp.bind(this);
    this.htResize = this.htResize.bind(this);
    this.pageLoaded = this.pageLoaded.bind(this);
    this.onHoldSlide = this.onHoldSlide.bind(this);
    this.onLeaveSlide = this.onLeaveSlide.bind(this);
    this.onMouseMoving = this.onMouseMoving.bind(this);

    this.init();
    this.bindEvent();
  }

  init() {
    const DomItems = document.querySelectorAll(".js-projectPageItem");
    const DomHeading = document.querySelectorAll(".js-projectsPage_names_item");

    if (DomItems.length > 0) {
      const images = [];
      this.groupList = new THREE.Group();
      threeSketch.addGroup(this.groupList);

      DomItems.forEach((item, index) => {
        const projectItem = new ProjectItem({
          el: item,
          addGroup: this.addMesh.bind(this),
          elHeading: DomHeading[index],
          uniforms: this.uniforms,
          index,
          indexActive: this.itemActive,
          thumb: this.DOM.thumbnailItems[index],
        });
        this.projects.push(projectItem);
      });

      this.drag = new Drag({
        el: this.DOM.dragger,
        animationIn: null,
        updateDragValue: this.onDragging,
        updateProp: this.getProp,
        onHold: this.onHoldSlide,
        onUp: this.onLeaveSlide,
        onMouseMoving: this.onMouseMoving,
      });

      setTimeout(() => {
        const { left } =
          this.DOM.thumbnailItems[this.itemActive].getBoundingClientRect();
        gsap.set(this.DOM.borderActive, { x: left });
      }, 50);

      threeSketch.addLoop(this.loop);
    }
  }

  onHoldSlide() {
    gsap.killTweensOf(this.drag.style.transition);
    this.projects.forEach((item, key) => {
      item.scaleIn(this.itemActive - key);
    });
    this.DOM.container.style.pointerEvents = "none";
    this.projects[this.itemActive].animationTextOut();
    this.DOM.borderActive.classList.add("on-hover");
  }

  onLeaveSlide() {
    this.itemActive = Math.min(
      Math.max(this.itemActive, 0),
      this.projects.length - 1
    );
    this.projects.forEach((item) => {
      item.scaleOut();
    });

    this.projects[this.itemActive].animationTextIn();
    this.DOM.container.style.pointerEvents = null;
    gsap.to(this.drag.style.transition, {
      last: -this.projects[this.itemActive].getFocus(),
      duration: 1,
      ease: "power3.out",
      onUpdate: () => {
        this.uniforms.u_strength.value = 0;
      },
    });
    this.DOM.borderActive.classList.remove("on-hover");
  }

  getProp() {
    this.drag.style.transition.maxDrag = -1;
    this.styles.triggerStart = services.winSize.width / 2 - 100;
    this.styles.triggerEnd = services.winSize.width / 2 + 100;
  }

  onDragging(current, streg) {
    this.projects.forEach((item, index) => {
      item.dragging(current);
    });
    this.draggingVal = Math.abs(current);
    this.strength.dragging = streg;
  }

  onMouseMoving(last) {
    this.projects.forEach((item, index) => {
      if (
        item.draggingTestActive(last, this.styles) &&
        index !== this.itemActive
      ) {
        this.itemActive = index;
        const { left } =
          this.DOM.thumbnailItems[this.itemActive].getBoundingClientRect();
        gsap.to(this.DOM.borderActive, {
          x: left,
          ease: "power3.out",
          duration: 0.4,
        });
      }
    });
  }

  addMesh(mesh) {
    this.groupList.add(mesh);
  }

  handleScrolling({ instance }) {
    if (this.projects.length) {
      this.projects.forEach((item) => {
        item.handleScrolling(instance);
      });

      threeSketch.render();
    }
  }

  loop() {
    this.strength.current = MathLerp(
      this.strength.current,
      Math.abs(services.smooth_scrolling_instance),
      this.strength.ease
    );

    const drag = MathMap(this.strength.dragging, 0, 50, 0, 10);
    const scroll = MathMap(
      Math.abs(
        Math.abs(services.smooth_scrolling_instance) - this.strength.current
      ),
      0,
      100,
      0,
      25
    );

    this.uniforms.u_strength.value = Math.max(Math.max(drag, scroll), 10);
  }

  pageLoaded() {
    this.getProp();
    this.drag.style.transition.last = this.drag.style.transition.current =
      -this.projects[this.itemActive].getFocus();
    setTimeout(() => {
      this.projects[this.itemActive].animationTextIn();
    }, 400);
  }

  htResize() {
    this.drag.style.transition.last = this.drag.style.transition.current =
      -this.projects[this.itemActive].getFocus();
    this.drag.renderDrag();
    const { left } =
      this.DOM.thumbnailItems[this.itemActive].getBoundingClientRect();
    gsap.set(this.DOM.borderActive, { x: left });
  }

  itemOnclick(key) {
    if (this.itemActive === key) return;

    this.projects[this.itemActive].animationTextOut();

    this.itemActive = key;
    this.onLeaveSlide();

    const { left } =
      this.DOM.thumbnailItems[this.itemActive].getBoundingClientRect();
    gsap.to(this.DOM.borderActive, {
      x: left,
      ease: "power3.out",
      duration: 0.4,
    });
  }

  nextItem() {
    const nextIndex = (this.itemActive + 1) % this.projects.length; // Loop back to the first item
    this.itemOnclick(nextIndex);
  }

  previousItem() {
    const prevIndex =
      (this.itemActive - 1 + this.projects.length) % this.projects.length; // Loop to the last item
    this.itemOnclick(prevIndex);
  }

  bindEvent() {
    EmitEvent.on(SMOOTH_SCROLLING, this.handleScrolling);
    EmitEvent.on(HT_RESIZE, this.htResize);
    EmitEvent.on(PAGE_LOADED, this.pageLoaded);
    this.DOM.thumbnailItems.forEach((item, key) => {
      item.addEventListener("click", () => this.itemOnclick(key));
    });
    this.DOM.controlLeft?.addEventListener("click", () => this.previousItem());
    this.DOM.controlRight?.addEventListener("click", () => this.nextItem());
  }

  removeEvent() {
    EmitEvent.off(HT_RESIZE, this.htResize);
    EmitEvent.off(PAGE_LOADED, this.pageLoaded);
    if (this.drag) {
      this.drag.removeEvent();
    }
    threeSketch.removeGroup(this.groupList);
    threeSketch.removeLoop(this.loop);
    this.projects.forEach((item) => {
      item.removeEvent();
    });
    EmitEvent.off(SMOOTH_SCROLLING, this.handleScrolling);
  }
}
